import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import ErrorModal from "../../../reusableComponents/ErrorModal";
import SuccessModal from "../../../reusableComponents/SuccessModal";
import {removeLastPayment} from "../../../store/reducers/accountSlice";
import {getPaymentStatus} from "../../../utils/apis";
import "./index.scss";
import {getPaymentStatusPublic} from "../../../utils/apis/payment";
import ProcessingModal from "../../../reusableComponents/processing modal";

import Confetti from "react-confetti";
import successSound from "../../../assets/SoundFiles/tada-fanfare-a-6313.mp3";
import {useWindowSize} from "react-use";

function PostPayment({status}) {
  const reduxChallengeId = useSelector((state) => state.account.selectedChallenge);
  const paymentHistory = useSelector((state) => state.account.paymentHistory);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const {idToken} = useSelector((state) => state.auth);

  const lastPayment = paymentHistory?.lastPayment || JSON.parse(localStorage.getItem("lastPayment") || "null");
  const challengeId = reduxChallengeId || localStorage.getItem("checkout_challenge_id") || lastPayment?.data?.challenge_id || lastPayment?.data?.challenge;

  const {width, height} = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);

  const playSuccessSound = () => {
    try {
      const audio = new Audio(successSound);
      audio.volume = 0.6;
      audio.play().catch((e) => {
        console.warn("Sound autoplay blocked by browser policy:", e);
      });
    } catch (e) {
      console.warn("Sound autoplay blocked:", e);
    }
  };

  const handleModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    setModalOpen(true);
  }, [status]);

  const [response, setResponse] = useState(null);

  useEffect(() => {
    if (!idToken && !lastPayment?.data?.email) {
      window.location.href = "/signin";
      return;
    }
    const getPaymentStatusForChallenge = async () => {
      if (lastPayment?.data?.payment_mode === "rapyd") {
        if (status === "success") {
          setPaymentStatus("success");
          localStorage.removeItem("checkout_details");
          localStorage.removeItem("checkout_challenge_id");

          // 🎉 CONFETTI + SOUND
          setShowConfetti(true);
          playSuccessSound();
          window.fbq("track", "Purchase", {
            currency: "USD",
            value: lastPayment?.data?.amount,
          });

          await new Promise((resolve) => setTimeout(resolve, 2000));
          dispatch(removeLastPayment());
          localStorage.removeItem("lastPayment");
          navigate("/funding-evaluation-payment/payment-history/");
          return;
        } else {
          setPaymentStatus("processing");
          await new Promise((resolve) => setTimeout(resolve, 10000));
          return;
        }
      }

      try {
        setIsLoading(true);
        const payloadPublic = {payment_id: lastPayment?.data?.id, email: lastPayment?.data?.email};
        const payload = {payment_id: lastPayment?.data?.id};

        // Simulate delay for processing
        await new Promise((resolve) => setTimeout(resolve, 15000));

        if (idToken) {
          // Handle token scenario
          const responseAPI = await getPaymentStatus(idToken, payload);

          setResponse(responseAPI?.response?.data);

          window.fbq("track", "Purchase", {
            currency: "USD",
            value: lastPayment?.data?.amount,
          });

          if (responseAPI?.status === 200) {
            setPaymentStatus("success");
            localStorage.removeItem("checkout_details");
            localStorage.removeItem("checkout_challenge_id");

            // 🎉 CONFETTI + SOUND
            setShowConfetti(true);
            playSuccessSound();
            window.fbq("track", "Purchase", {currency: "USD", value: lastPayment?.data?.amount});

            await new Promise((resolve) => setTimeout(resolve, 2000));

            navigate("/funding-evaluation-payment/payment-history/");
          } else {
            throw new Error("Payment status not successful");
          }
        } else {
          const responseAPI = await getPaymentStatusPublic(payloadPublic);
          setResponse(responseAPI?.response?.data);
          // Handle no token scenario

          setPaymentStatus("failure");
          setResponse({detail: "No authentication token found"});

          // Redirect to sign-in with message
          await new Promise((resolve) => setTimeout(resolve, 10000));
          navigate("/signin", {state: {message: "Your payment is being processed! Please visit your dashboard payment history to check transaction status."}});
        }
      } catch (error) {
        console.error("Error fetching payment status:", error);
        setIsLoading(false);
        setPaymentStatus("failure");
        await new Promise((resolve) => setTimeout(resolve, 10000));
        navigate(`/funding-evaluation/payments/${challengeId}`);
      } finally {
        dispatch(removeLastPayment());
        localStorage.removeItem("lastPayment");
        setIsLoading(false);
      }
    };

    getPaymentStatusForChallenge();
  }, [idToken, navigate, dispatch, paymentHistory, challengeId, status]);

  useEffect(() => {
    if (paymentStatus === "success") {
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  const accountList = useSelector((state) => state?.account?.accountList?.data);
  let msg = accountList?.length > 0 ? "Congratulations! Payment was successful" : "You'll shortly receive an email with your account login credentials";

  if (!idToken && !lastPayment?.data?.email) {
    return null;
  }

  return (
    <>
      {paymentStatus === "success" && showConfetti && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={350}
          recycle={false}
          gravity={0.5}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 9999,
            pointerEvents: "none",
          }}
        />
      )}

      {isLoading && <ProcessingModal open={isLoading} />}
      {!isLoading &&
        isModalOpen &&
        (paymentStatus === "success" ? (
          <SuccessModal
            setModalOpen={handleModal}
            title="Successful!"
            desc={msg}
            btnText="Close"
          />
        ) : (
          <ErrorModal
            handleNavigation={() => navigate(`/funding-evaluation/payments/${challengeId}`)}
            setModalOpen={handleModal}
            desc={
              response?.detail === "Unable to fetch the payment status" || lastPayment?.data?.payment_mode === "rapyd"
                ? "Payment is in progress, if successful you will receive your account in 15-30 mins"
                : "Payment is in progress."
            }
            btnText="Close"
          />
        ))}
    </>
  );
}

export default PostPayment;
